<template>
  <HeaderBar />
  <section class="carvia-container relative">
    <div class="mb-4 grid grid-cols-3 items-center gap-4">
      <Input v-model="searchQuery" :placeholder="t('search')" class="flex-1" />
      <Input
        v-model="licencePlateFinSearchQuery"
        :placeholder="t('searchForLicencePlateFin')"
        class="flex-1"
      />
      <CVButton
        class="flex-1"
        @click="
          $router.push({
            name: 'editVehicleType',
            params: { id: 'new' },
          })
        "
        >{{ t('addVehicleType') }}</CVButton
      >
    </div>

    <div class="mb-4 grid grid-cols-3 gap-4">
      <div class="col-span-2 flex items-center justify-between gap-4">
        <div class="flex gap-4">
          <CVButton
            :disabled="activeTabGroup === TabGroup.FILTER"
            :variant="activeTabGroup === TabGroup.FILTER ? 'ghost' : 'secondary'"
            @click.prevent="activeTabGroup = TabGroup.FILTER"
            >{{ t('showFilters') }}</CVButton
          >
          <CVButton
            :disabled="activeTabGroup === TabGroup.TODO"
            :variant="activeTabGroup === TabGroup.TODO ? 'ghost' : 'secondary'"
            @click.prevent="activeTabGroup = TabGroup.TODO"
            >{{ t('showTodos') }}</CVButton
          >
        </div>
        <StationLegalEntitySwitcher
          :model-value="stationId ?? undefined"
          @update:model-value="stationId = $event ?? null"
        />
      </div>
      <div>
        <CVButton
          class="w-full"
          @click="
            $router.push({
              name: 'addNewCar',
            })
          "
          >{{ t('addCar') }}</CVButton
        >
      </div>
    </div>

    <div class="mb-6">
      <Tabs
        v-if="activeTabGroup === TabGroup.FILTER"
        v-model="activeTab"
        :tabs="tabsFilter"
        class="mt-6"
      />
      <Tabs
        v-if="activeTabGroup === TabGroup.TODO"
        v-model="activeTab"
        :tabs="tabsTodo"
        class="mt-6"
        slim
      />
      <Tabs
        v-if="activeTabGroup === TabGroup.TODO && activeTab === Tab.WORKSHOP_APPOINTMENTS"
        v-model="activeWorkshopSubTab"
        :tabs="workshopSubTabs"
        class="mt-4"
        query-param="workshop-sub-tab"
      />
    </div>

    <div v-if="activeTab === Tab.WORKSHOP_APPOINTMENTS" class="mb-4 flex justify-end">
      <Select
        v-if="activeWorkshopSubTab === WorkshopAppointmentListType.REQUIRES_WORKSHOP"
        v-model="workshopRequirementReason"
        variant="ghost"
        :placeholder="t('workshopRequirementReason')"
        :options="workshopRequirementReasonOptions"
        empty-value-to="null"
        class="w-48"
      />
      <MultiSelect
        v-else
        v-model="workshopAppointmentTypeFilter"
        variant="ghost"
        :options="workshopAppointmentTypeFilterOptions"
        :placeholder="t('workshopAppointmentType')"
        class="w-72"
      />
    </div>

    <div
      v-if="activeTab in infoBoxTexts"
      class="border-primary/50 mb-4 flex items-center gap-3 border p-2 pb-1 pt-1.5"
    >
      <div
        class="flex h-8 w-8 flex-none items-center justify-center rounded-full border border-primary text-2xl text-primary"
      >
        i
      </div>
      <p class="text-sm">{{ infoBoxTexts[activeTab] }}</p>
    </div>

    <div v-if="activeTab === Tab.GHOST_CARS" class="mb-4 grid grid-cols-3 gap-4">
      <CVButton
        class="col-start-3"
        @click="
          router.push({
            name: 'addNewCar',
            query: { isGhostCar: 'true' },
          })
        "
        >{{ t('addGhostCar') }}</CVButton
      >
    </div>

    <UnhandledDamagesBookingList v-if="showUnhandledDamages" />
    <div v-else-if="vehicleTypes && showVehicleTypes" class="flex flex-col space-y-2">
      <VehicleTypeCard
        v-for="vehicleType in vehicleTypes"
        :key="vehicleType.id"
        :vehicle-type="vehicleType"
      />
    </div>
    <div v-else>
      <div v-if="activeTab === Tab.ORDERED" class="space-y-2">
        <div v-for="(month, i) in carsPerMonth" :key="i" class="space-y-2">
          <div class="text-sm text-primary">
            <span class="font-medium">{{ formatDate(month.month, 'LLLL') }}</span>
            {{ formatDate(month.month, 'yyyy') }}
          </div>
          <div class="space-y-2">
            <CarCard
              v-for="car in month.cars"
              :key="car.id"
              :car="car"
              :vehicle-type="car.vehicleType"
              :drop-down-items="todoDropDownItems(car.id)"
              :on-click-function="getTodoFunction(car)"
            />
          </div>
        </div>
      </div>
      <div v-else class="space-y-2">
        <template
          v-if="
            activeTab === Tab.WORKSHOP_APPOINTMENTS &&
            activeWorkshopSubTab !== WorkshopAppointmentListType.REQUIRES_WORKSHOP
          "
        >
          <CarCard
            v-for="workshopAppointment in workshopAppointments"
            :key="workshopAppointment.id"
            :car="workshopAppointment.car"
            :vehicle-type="workshopAppointment.car.vehicleType"
            :workshop-appointment="workshopAppointment"
            :drop-down-items="todoDropDownItems(workshopAppointment.car.id, workshopAppointment)"
            :on-click-function="getTodoFunction(workshopAppointment.car, workshopAppointment)"
          />
        </template>
        <template v-else>
          <CarCard
            v-for="car in visibleCars"
            :key="car.id"
            :car="car"
            :vehicle-type="car.vehicleType"
            :drop-down-items="todoDropDownItems(car.id)"
            :on-click-function="getTodoFunction(car)"
            :variant="activeTab === Tab.TYRE_CONDITION ? getTyreCardVariant(car) : 'default'"
          />
        </template>
      </div>

      <div
        v-if="
          activeTab === Tab.WORKSHOP_APPOINTMENTS &&
          activeWorkshopSubTab !== WorkshopAppointmentListType.REQUIRES_WORKSHOP &&
          workshopAppointmentsHasNextPage
        "
        class="flex justify-center pt-6"
      >
        <CVButton
          outline
          :is-loading="workshopAppointmentsIsFetchingNextPage"
          @click="() => workshopAppointmentsFetchNextPage()"
        >
          {{ t('loadMore') }}
        </CVButton>
      </div>
      <div v-else-if="carsHasNextPage" class="flex justify-center pt-6">
        <CVButton outline :is-loading="carsIsFetchingNextPage" @click="() => carsFetchNextPage()">
          {{ t('loadMore') }}
        </CVButton>
      </div>
    </div>

    <EntityListExceptions
      v-if="showVehicleTypes"
      :status="vehicleTypesStatus"
      :error="vehicleTypesError"
      :items-count="vehicleTypes?.length ?? 0"
      :no-items-text="t('noVehicleTypeFound')"
    />

    <EntityListExceptions
      v-if="showCars"
      :status="carListStatus"
      :error="carListError"
      :items-count="carListCount"
      :no-items-text="t('noCarFound')"
    />

    <div v-if="isError">
      <p>{{ t('somethingWentWrong') }}</p>
    </div>

    <TodosModal v-model="showTodosModal" :car="currentTodoCar" :active-tab="activeTab" />

    <div
      v-if="isUpdatingCar"
      class="absolute inset-0 z-10 flex items-center justify-center bg-black/20"
    >
      <Spinner />
    </div>
  </section>
</template>

<script lang="ts" setup>
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import Input from '@/components/Input.vue';
import type { FindAllVehicleTypesQuery } from '@/entities/vehicle-type.entity';
import { useI18n } from 'vue-i18n';
import { useVehicleTypesWithQuery } from '@/queries/use-vehicle-types';
import { useUiStore } from '@/stores/ui.store';
import { computed, ref, watch, watchEffect } from 'vue';
import VehicleTypeCard from '@/views/vehicle-types/vehicle-types-list/VehicleTypeCard.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import Select from '@/components/Select.vue';
import { workshopAppointmentTypeOrder } from '@/constants/workshop-appointment-type-order.constant';
import { useStations } from '@/queries/use-stations';
import StationLegalEntitySwitcher from '@/components/StationLegalEntitySwitcher.vue';
import { useLegalEntityFilter } from '@/hooks/use-legal-entity-filter';
import { useRouter } from 'vue-router';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import CarCard from '@/views/vehicle-types/vehicle-types-list/CarCard.vue';
import { useCarsWithQuery } from '@/queries/use-cars';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import Tabs, { type TabItem } from '@/components/Tabs.vue';
import { VehiclePoolTab as Tab } from './vehicle-pool-tab.enum';
import { useCarsQuery } from './use-cars-query';
import TodosModal from './components/TodosModal.vue';
import UnhandledDamagesBookingList from './components/UnhandledDamagesBookingList.vue';
import { DateTime } from 'luxon';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import { CarStatus } from '@/entities/cars/car-status.enum';
import { useTodoFunctions } from './use-todo-functions';
import type { Car } from '@/entities/cars/car.entity';
import { refDebounced } from '@vueuse/core';
import {
  TyreCondition,
  WorkshopAppointmentListType,
  WorkshopAppointmentType,
} from '@carvia/ros-client-types';
import type { ListItemVariant } from '@/components/ListItem.vue';
import { formatDate } from '@/hooks/use-formatted-date.ts';
import { getCurrentCarTyresSeason } from '@/utils/get-current-car-tyres-season.ts';
import { type WorkshopAppointment } from '@/entities/workshop-appointments/workshop-appointment.entity';
import {
  useUpdateWorkshopAppointment,
  useWorkshopAppointments,
} from '@/queries/use-workshop-appointments';

enum TabGroup {
  FILTER = 'FILTER',
  TODO = 'TODO',
}

const { t } = useI18n();
const router = useRouter();

const stationId = useRouteQueryRef('stationId', null, 'string', { nullQueryValue: 'ALL' });
const searchQuery = useRouteQueryRef('search', '', 'string');
const searchQueryDebounced = refDebounced(searchQuery, 500);
const licencePlateFinSearchQuery = useRouteQueryRef('licencePlateFin', '', 'string');
const licencePlateFinSearchQueryDebounced = refDebounced(licencePlateFinSearchQuery, 500);
const activeTabGroup = useRouteQueryRef<TabGroup>('tab-group', TabGroup.FILTER, 'string');

const activeTab = ref<Tab>(Tab.OVERVIEW);
const tabsFilter = computed<TabItem[]>(() => [
  { title: t('overview'), id: Tab.OVERVIEW },
  { title: t('ordered'), id: Tab.ORDERED },
  { title: t('floatIn'), id: Tab.FLOAT_IN },
  { title: t('floatOut'), id: Tab.FLOAT_OUT },
  { title: t('ghostCars'), id: Tab.GHOST_CARS },
]);
const tabsTodo = computed<TabItem[]>(() => [
  { title: t('merchantConsultation'), id: Tab.MERCHANT_CONSULTATION },
  { title: t('carIncoming'), id: Tab.CAR_INCOMING },
  { title: t('workshopAppointments'), id: Tab.WORKSHOP_APPOINTMENTS },
  { title: t('tyreCondition'), id: Tab.TYRE_CONDITION },
  { title: t('tyresSeason'), id: Tab.TYRES_SEASON },
  { title: t('sell'), id: Tab.SELL },
  { title: t('unhandledDamages'), id: Tab.UNHANDLED_DAMAGES },
]);
const activeWorkshopSubTab = ref<WorkshopAppointmentListType>(
  WorkshopAppointmentListType.REQUIRES_WORKSHOP,
);
const workshopSubTabs = computed<TabItem[]>(() => {
  const tabs: TabItem[] = [
    {
      title: t('workshopSubtabs.requiresWorkshop'),
      id: WorkshopAppointmentListType.REQUIRES_WORKSHOP,
    },
    { title: t('workshopSubtabs.upcoming'), id: WorkshopAppointmentListType.UPCOMING },
    { title: t('workshopSubtabs.atWorkshop'), id: WorkshopAppointmentListType.AT_WORKSHOP },
    {
      title: t('workshopSubtabs.readyForPickup'),
      id: WorkshopAppointmentListType.READY_FOR_PICKUP,
    },
  ];
  if (licencePlateFinSearchQueryDebounced.value?.trim()) {
    tabs.unshift({ title: t('workshopSubtabs.all'), id: WorkshopAppointmentListType.ALL });
  }
  return tabs;
});
const workshopRequirementReason = ref<WorkshopAppointmentType | null>(null);
const workshopRequirementReasonOptions = computed(() => [
  {
    label: t('workshopRequirementReasonAll'),
    value: null,
  },
  {
    label: t('workshopAppointmentTypes.INSPECTION'),
    value: WorkshopAppointmentType.INSPECTION,
  },
  {
    label: t('workshopAppointmentTypes.TUV'),
    value: WorkshopAppointmentType.TUV,
  },
]);
const workshopAppointmentTypeFilter = ref<WorkshopAppointmentType[]>([]);
const workshopAppointmentTypeFilterOptions = computed(() =>
  workshopAppointmentTypeOrder.map((value) => ({
    label: t(`workshopAppointmentTypes.${value}`),
    value,
  })),
);

const showVehicleTypes = computed(
  () =>
    !!(
      (activeTab.value === Tab.OVERVIEW && !licencePlateFinSearchQuery.value) ||
      searchQueryDebounced.value
    ),
);
const showUnhandledDamages = computed(() => activeTab.value === Tab.UNHANDLED_DAMAGES);
const showCars = computed(() => !showVehicleTypes.value && !showUnhandledDamages.value);

watch(activeTabGroup, () => {
  activeTab.value =
    activeTabGroup.value === TabGroup.FILTER ? Tab.OVERVIEW : Tab.MERCHANT_CONSULTATION;
});

const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);

const { legalEntityId } = useLegalEntityFilter();
const legalEntityStations = computed(() =>
  (stations.value ?? []).filter(
    (station) => !legalEntityId.value || station.legalEntity?.id === legalEntityId.value,
  ),
);

// A selected station narrows the list further; otherwise the legal entity filter limits it to
// that legal entity's stations.
const filteredStationIds = computed(() => {
  if (stationId.value) return [stationId.value];
  if (legalEntityId.value) return legalEntityStations.value.map((station) => station.id);
  return undefined;
});

watch(legalEntityId, () => {
  if (
    stationId.value &&
    !legalEntityStations.value.some((station) => station.id === stationId.value)
  ) {
    stationId.value = null;
  }
});

const query = computed<FindAllVehicleTypesQuery>(() => ({
  query: searchQueryDebounced.value,
  stationIds: filteredStationIds.value,
  includeNumCars: true,
}));
const {
  data: vehicleTypes,
  isError: isVehicleTypesError,
  status: vehicleTypesStatus,
  error: vehicleTypesError,
} = useVehicleTypesWithQuery(query, showVehicleTypes);

const carsQuery = useCarsQuery(
  activeTab,
  activeWorkshopSubTab,
  licencePlateFinSearchQueryDebounced,
  filteredStationIds,
  workshopRequirementReason,
);
const useCarsDataSource = computed(
  () =>
    activeTab.value !== Tab.WORKSHOP_APPOINTMENTS ||
    activeWorkshopSubTab.value === WorkshopAppointmentListType.REQUIRES_WORKSHOP,
);
const {
  data: carsData,
  isError: isCarsError,
  fetchNextPage: carsFetchNextPage,
  hasNextPage: carsHasNextPage,
  isFetchingNextPage: carsIsFetchingNextPage,
  status: carsStatus,
  error: carsError,
} = useCarsWithQuery(
  carsQuery,
  computed(() => showCars.value && useCarsDataSource.value),
);
const cars = useFlattenPaginatedData(carsData);
const workshopQuery = computed(() => ({
  type: activeWorkshopSubTab.value,
  licencePlateOrFin: licencePlateFinSearchQueryDebounced.value || undefined,
  appointmentTypes: workshopAppointmentTypeFilter.value.length
    ? workshopAppointmentTypeFilter.value
    : undefined,
}));
const {
  data: workshopAppointmentsData,
  fetchNextPage: workshopAppointmentsFetchNextPage,
  hasNextPage: workshopAppointmentsHasNextPage,
  isFetchingNextPage: workshopAppointmentsIsFetchingNextPage,
  status: workshopAppointmentsStatus,
  error: workshopAppointmentsError,
} = useWorkshopAppointments(
  workshopQuery,
  computed(
    () =>
      showCars.value && activeTab.value === Tab.WORKSHOP_APPOINTMENTS && !useCarsDataSource.value,
  ),
);
const workshopAppointments = useFlattenPaginatedData(workshopAppointmentsData);

const getTyreCardVariant = (car: Car): ListItemVariant => {
  if (
    car.tyreConditionFront === TyreCondition.NEEDS_IMMEDIATE_CHANGE ||
    car.tyreConditionRear === TyreCondition.NEEDS_IMMEDIATE_CHANGE
  ) {
    return 'danger';
  } else if (
    car.tyreConditionFront === TyreCondition.NEAR_WEAR_LIMIT ||
    car.tyreConditionRear === TyreCondition.NEAR_WEAR_LIMIT
  ) {
    return 'problem';
  }
  return 'default';
};

const visibleCars = computed(() =>
  cars.value.filter((car) => {
    if (
      (activeTab.value !== Tab.MERCHANT_CONSULTATION &&
        activeTab.value !== Tab.CAR_INCOMING &&
        activeTab.value !== Tab.WORKSHOP_APPOINTMENTS &&
        activeTab.value !== Tab.TYRE_CONDITION &&
        activeTab.value !== Tab.TYRES_SEASON &&
        activeTab.value !== Tab.SELL) ||
      (activeTab.value === Tab.MERCHANT_CONSULTATION && !car.merchantConsultedDate) ||
      (activeTab.value === Tab.CAR_INCOMING && !car.registrationAndOrganizationDoneDate) ||
      (activeTab.value === Tab.WORKSHOP_APPOINTMENTS &&
        activeWorkshopSubTab.value === WorkshopAppointmentListType.REQUIRES_WORKSHOP) ||
      (activeTab.value === Tab.TYRE_CONDITION &&
        (car.tyreConditionFront !== TyreCondition.GOOD ||
          car.tyreConditionRear !== TyreCondition.GOOD)) ||
      (activeTab.value === Tab.TYRES_SEASON && car.tyres !== getCurrentCarTyresSeason()) ||
      (activeTab.value === Tab.SELL && car.status === CarStatus.FLOAT_IN)
    ) {
      return true;
    }
    return false;
  }),
);

const carsPerMonth = computed(() => {
  return visibleCars.value
    .reduce(
      (months, car) => {
        const monthDate = DateTime.fromISO(car.availableFrom).startOf('month').toISO()!;
        const month = months.find((month) => month.month === monthDate);
        if (!month) {
          months.push({
            month: monthDate,
            cars: [car],
          });
        } else {
          month.cars.push(car);
        }
        return months;
      },
      [] as { month: string; cars: Car[] }[],
    )
    .sort((a, b) => (DateTime.fromISO(a.month) > DateTime.fromISO(b.month) ? 1 : -1));
});

const isError = computed(
  () =>
    (!showVehicleTypes.value && isVehicleTypesError.value) ||
    (showCars.value &&
      ((useCarsDataSource.value && isCarsError.value) ||
        (!useCarsDataSource.value && !!workshopAppointmentsError.value))),
);

const carListStatus = computed(() =>
  useCarsDataSource.value ? carsStatus.value : workshopAppointmentsStatus.value,
);
const carListError = computed(() =>
  useCarsDataSource.value ? carsError.value : workshopAppointmentsError.value,
);
const carListCount = computed(() =>
  useCarsDataSource.value ? cars.value.length : workshopAppointments.value.length,
);

const { getTodoFunction, showTodosModal, currentTodoCar, isUpdatingCar } = useTodoFunctions(
  activeTab,
  t,
);

const { mutateAsync: updateWorkshopAppointment } = useUpdateWorkshopAppointment();

const todoDropDownItems = (carId: string, workshopAppointment?: WorkshopAppointment) => {
  const items = [
    {
      title: t('editCar'),
      onClick: () =>
        router.push({
          name: 'editCar',
          params: { id: carId },
        }),
    },
  ];
  if (
    activeTab.value === Tab.WORKSHOP_APPOINTMENTS &&
    activeWorkshopSubTab.value === WorkshopAppointmentListType.AT_WORKSHOP &&
    workshopAppointment &&
    !workshopAppointment.readyForPickup
  ) {
    items.push({
      title: t('markAsReadyForPickup'),
      onClick: async () => {
        await updateWorkshopAppointment({
          id: workshopAppointment.id,
          dto: { readyForPickup: true },
        });
      },
    });
  }
  return items;
};

const uiStore = useUiStore();
watchEffect(() => {
  uiStore.setHeaderTitle(t('vehiclePoolManagement'));
});

const infoBoxTexts = computed<Record<string, string>>(() => ({
  [Tab.MERCHANT_CONSULTATION]: t('infoBoxTexts.merchantConsultation'),
  [Tab.CAR_INCOMING]: t('infoBoxTexts.carIncoming'),
  [Tab.TYRE_CONDITION]: t('infoBoxTexts.tyres'),
  [Tab.TYRES_SEASON]: t('infoBoxTexts.tyresSeason'),
  [Tab.SELL]: t('infoBoxTexts.sell'),
}));
</script>

<i18n lang="json">
{
  "en": {
    "vehiclePoolManagement": "Vehicle Pool Management",
    "addVehicleType": "+ Add Vehicle Type",
    "addCar": "+ Add Car",
    "addGhostCar": "+ Add Ghost Car",
    "search": "Search vehicle type",
    "somethingWentWrong": "Something went wrong",
    "searchForLicencePlateFin": "Search car (plate/FIN/fleet no.)",
    "showFilters": "Show Filters",
    "showTodos": "Show Todos",
    "overview": "Overview",
    "ordered": "Ordered",
    "floatIn": "Float In",
    "floatOut": "Float Out",
    "ghostCars": "Ghost Cars",
    "merchantConsultation": "Merchant Consultation",
    "carIncoming": "Car Incoming",
    "workshopAppointments": "Workshop/Maintenance appointment",
    "workshopRequirementReason": "Reason",
    "workshopRequirementReasonAll": "All reasons",
    "workshopAppointmentType": "Appointment type",
    "workshopAppointmentTypeAll": "All types",
    "workshopSubtabs": {
      "requiresWorkshop": "Vehicle requires workshop/maintenance appointment",
      "upcoming": "Upcoming appointments",
      "atWorkshop": "Vehicle at workshop",
      "readyForPickup": "Vehicle ready for pickup",
      "all": "All"
    },
    "tyreCondition": "Tyre condition",
    "tyresSeason": "Tyres Season",
    "sell": "Sell",
    "unhandledDamages": "Unhandled Damages",
    "noVehicleTypeFound": "No vehicle type found",
    "noCarFound": "No car found",
    "changedToSummerTyres": "Confirm changed to summer tyres",
    "changedToWinterTyres": "Confirm changed to winter tyres",
    "confirmMerchantConsultation": "Confirm merchant consultation",
    "confirmRegistration": "Confirm registration",
    "enterTuvAppointmentDate": "Enter TÜV appointment date",
    "fleetOutCar": "Fleet out car",
    "reallyChangedTyresToSummer": "Have you really changed to summer tyres?",
    "reallyChangedTyresToWinter": "Have you really changed to winter tyres?",
    "yesSummerTyresAreApplied": "Yes, summer tyres are applied",
    "yesWinterTyresAreApplied": "Yes, winter tyres are applied",
    "editCar": "Edit Car",
    "markAsReadyForPickup": "Mark as ready for pickup",
    "infoBoxTexts": {
      "merchantConsultation": "Here are all ordered cars that will be delivered within the next 6 weeks according to the purchase contract. So check if something has changed.",
      "carIncoming": "Here are all ordered cars that will be delivered within the next 10 days according to the purchase contract. So organize the registration quickly, consult with accounting whether the car will be paid for/financed and organize the delivery to the right rental hub.",
      "tyres": "Here are all cars for which the tyres/wheels have to be changed due to insufficient tread depth.",
      "tyresSeason": "Here are all cars for which the tyres/wheels have to be changed. From October to the end of March these are all cars that are still on summer tyres and from April to the end of September all cars that are still equipped with winter tyres. (Some cars have special regulations and stay on winter tyres all year round. These are therefore not displayed here. The special regulation can be set in the Vehicle Type)",
      "sell": "Here are all cars that have reached 60,000 km or are 2 years old. This does not mean that the cars have to be sold immediately, but is intended as a hint to decide now whether the vehicle should be sold or not."
    }
  },
  "de": {
    "vehiclePoolManagement": "Fuhrparkmanagement",
    "addVehicleType": "+ Vehicle Type hinzufügen",
    "addCar": "+ Auto hinzufügen",
    "addGhostCar": "+ Ghostcar hinzufügen",
    "search": "Vehicle Type suchen",
    "somethingWentWrong": "Etwas ist schief gelaufen",
    "searchForLicencePlateFin": "Auto suchen (Kennz./FIN/FP-Nr.)",
    "showFilters": "Filter anzeigen",
    "showTodos": "Todos anzeigen",
    "overview": "Übersicht",
    "ordered": "Bestellt",
    "floatIn": "Eingeflottet",
    "floatOut": "Ausgeflottet",
    "ghostCars": "Ghostcars",
    "merchantConsultation": "Händler Rücksprache",
    "carIncoming": "Im Anflug",
    "workshopAppointments": "Werkstatt/Wartungstermin",
    "workshopRequirementReason": "Grund",
    "workshopRequirementReasonAll": "Alle Gründe",
    "workshopAppointmentType": "Terminart",
    "workshopAppointmentTypeAll": "Alle Arten",
    "workshopSubtabs": {
      "requiresWorkshop": "Fahrzeug benötigt Werkstatt / Wartungstermin",
      "upcoming": "Anstehende Termine",
      "atWorkshop": "Fahrzeug bei Werkstatt",
      "readyForPickup": "Fahrzeug abholbereit",
      "all": "Alle"
    },
    "tyreCondition": "Reifenzustand",
    "tyresSeason": "Reifen Saison",
    "sell": "Verkaufen",
    "unhandledDamages": "Unbehandelte Schäden",
    "noVehicleTypeFound": "Keine Vehicle Types gefunden",
    "noCarFound": "Keine Autos gefunden",
    "changedToSummerTyres": "Auf Sommerreifen gewechselt bestätigen",
    "changedToWinterTyres": "Auf Winterreifen gewechselt bestätigen",
    "confirmMerchantConsultation": "Händler Rücksprache bestätigen",
    "confirmRegistration": "Anmeldung bestätigen",
    "enterTuvAppointmentDate": "TÜV Termin eintragen",
    "fleetOutCar": "Auto ausflotten",
    "reallyChangedTyresToSummer": "Hast du wirklich auf Sommerreifen gewechselt?",
    "reallyChangedTyresToWinter": "Hast du wirklich auf Winterreifen gewechselt?",
    "yesSummerTyresAreApplied": "Ja, Sommerreifen wurden angebracht",
    "yesWinterTyresAreApplied": "Ja, Winterreifen wurden angebracht",
    "editCar": "Auto bearbeiten",
    "markAsReadyForPickup": "Als abholbereit markieren",
    "infoBoxTexts": {
      "merchantConsultation": "Hier befinden sich alle bestellten Autos die laut Kaufvertrag innerhalb der nächsten 6 Wochen geliefert werden. Also prüfe nach ob sich da nicht was geändert hat.",
      "carIncoming": "Hier befinden sich alle bestellten Autos die laut Kaufvertrag innerhalb der nächsten 10 Tage geliefert werden. Also organisiere jetzt schnell die Zulassung, halte Rücksprache mit Accounting ob das Auto auch bezahlt/finanziert wird und organisiere die Anlieferung an den richtigen Rental Hub.",
      "tyres": "Hier befinden sich alle Autos bei denen die Reifen/Räder aufgrund von zu geringer Profiltiefe gewechselt werden müssen.",
      "tyresSeason": "Hier befinden sich alle Autos bei denen die Reifen/Räder gewechselt werden müssen. Von Oktober bis Ende März sind das alle Autos die noch auf Sommerreifen stehen und von April bis Ende September alle die noch mit Winterreifen ausgestattet sind. (Manche Autos haben spezielle Regelungen und bleiben das ganze Jahr auf Winterreifen. Diese werden dementsprechend hier nicht angezeigt. Die Sonderregelung kann man im Vehicle Type einstellen)",
      "sell": "Hier befinden sich alle Autos die entweder 60.000 km erreicht haben oder 2 Jahre alt sind. Das heißt nicht, dass die Autos sofort verkauft werden müssen, sondern ist als Hinweis gedacht, damit man jetzt darüber entscheiden kann, ob das Fahrzeug nun verkauft werden soll oder nicht."
    }
  },
  "es": {
    "vehiclePoolManagement": "Gestión de flota",
    "addVehicleType": "+ Añadir tipo de vehículo",
    "addCar": "+ Añadir vehículo",
    "addGhostCar": "+ Añadir ghost car",
    "search": "Buscar tipo de vehículo",
    "somethingWentWrong": "Algo ha salido mal",
    "searchForLicencePlateFin": "Buscar vehículo (matr./VIN/n.º flota)",
    "showFilters": "Mostrar filtros",
    "showTodos": "Mostrar pendientes",
    "overview": "Vista general",
    "ordered": "Pedidos",
    "floatIn": "Alta en flota",
    "floatOut": "Baja de flota",
    "ghostCars": "Ghost cars",
    "merchantConsultation": "Consulta con concesionario",
    "carIncoming": "En llegada",
    "workshopAppointments": "Cita de taller / mantenimiento",
    "workshopRequirementReason": "Motivo",
    "workshopRequirementReasonAll": "Todos los motivos",
    "workshopAppointmentType": "Tipo de cita",
    "workshopAppointmentTypeAll": "Todos los tipos",
    "workshopSubtabs": {
      "requiresWorkshop": "El vehículo requiere cita de taller/mantenimiento",
      "upcoming": "Citas próximas",
      "atWorkshop": "Vehículo en taller",
      "readyForPickup": "Vehículo listo para recogida",
      "all": "Todas"
    },
    "unhandledDamages": "Daños sin tratar",
    "markAsReadyForPickup": "Marcar como listo para recogida",
    "inspection": "Inspección",
    "tuv": "ITV",
    "tyreCondition": "Estado de neumáticos",
    "tyresSeason": "Neumáticos por temporada",
    "sell": "Vender",
    "noVehicleTypeFound": "No se ha encontrado ningún tipo de vehículo",
    "noCarFound": "No se ha encontrado ningún vehículo",
    "changedToSummerTyres": "Confirmar cambio a neumáticos de verano",
    "changedToWinterTyres": "Confirmar cambio a neumáticos de invierno",
    "confirmMerchantConsultation": "Confirmar consulta con concesionario",
    "confirmRegistration": "Confirmar matriculación",
    "enterInspectionAppointmentDate": "Introducir cita de inspección",
    "enterTuvAppointmentDate": "Introducir cita de ITV",
    "fleetOutCar": "Dar de baja el vehículo de la flota",
    "reallyChangedTyresToSummer": "¿Has cambiado realmente a neumáticos de verano?",
    "reallyChangedTyresToWinter": "¿Has cambiado realmente a neumáticos de invierno?",
    "yesSummerTyresAreApplied": "Sí, los neumáticos de verano están montados",
    "yesWinterTyresAreApplied": "Sí, los neumáticos de invierno están montados",
    "editCar": "Editar vehículo",
    "infoBoxTexts": {
      "merchantConsultation": "Aquí están todos los vehículos pedidos que, según el contrato de compra, se entregarán en las próximas 6 semanas. Comprueba si algo ha cambiado.",
      "carIncoming": "Aquí están todos los vehículos pedidos que, según el contrato de compra, se entregarán en los próximos 10 días. Organiza rápido la matriculación, coordina con contabilidad el pago/financiación y organiza la entrega al hub de alquiler correcto.",
      "inspection": "Aquí están todos los vehículos que deben pasar inspección como máximo en los próximos 5.000 km",
      "tuv": "Aquí están todos los vehículos a los que les toca la ITV a partir de ahora.",
      "tyres": "Aquí están todos los vehículos en los que hay que cambiar neumáticos/ruedas por poco dibujo.",
      "tyresSeason": "Aquí están todos los vehículos en los que hay que cambiar neumáticos/ruedas por temporada. De octubre a finales de marzo: los que siguen con neumáticos de verano; de abril a finales de septiembre: los que siguen con neumáticos de invierno. (Algunos vehículos tienen reglas especiales y llevan invierno todo el año; no aparecen aquí. La regla especial se configura en el tipo de vehículo.)",
      "sell": "Aquí están todos los vehículos que han alcanzado 60.000 km o tienen 2 años. No significa que deban venderse ya, sino que es un recordatorio para decidir si conviene vender el vehículo o no."
    }
  }
}
</i18n>
